import { ImageScaleView, animate } from '@play-co/timestep-core/ui';
import ButtonScaleView from 'src/lib/ui/components/ButtonScaleView';
import { Feature } from 'src/replicant/analytics';
import StateObserver, { user } from 'src/StateObserver';
import { getPowerBlockMap } from '../game/defs/powerBlocks';
import { Match2ButtonBadge, Match2ButtonBadgeState } from './Match2ButtonBadge';
import { promptPurchasePowerup } from '../match2.sequence';
import { Match2PowerupType } from 'src/replicant/match2/match2.ruleset';
import sounds from 'src/lib/sounds';
import bitmapFonts from 'src/lib/bitmapFonts';
import uiConfig from 'src/lib/ui/config';
import { formatDurationShort } from 'src/lib/formatDuration';
import { createWaiter } from 'src/lib/utils';
import { duration } from 'src/replicant/utils/duration';
import { getMatch2TimedRewardRemainingTime } from 'src/replicant/match2/match2.getters';
import { LangBitmapFontTextShadowView } from 'src/lib/ui/components/LangBitmapFontTextShadowView';
import { getMatch2AssetPath } from '../game/util/assets';
import { isSuperLollipopActive } from 'src/replicant/superLollipop/superLollipop.getters';
import { SuperLollipopIcon } from 'src/game/superLollipop/components/SuperLollipopIcon';

export class Match2PowerupButton extends ButtonScaleView {
  private type!: Match2PowerupType;
  private locked = false;
  private infinite = false;
  private selected = false;
  private powerupIcon: ImageScaleView;
  private badge: Match2ButtonBadge;
  private timerBanner: ImageScaleView;
  private timer: LangBitmapFontTextShadowView;
  private waiter?: ReturnType<typeof createWaiter>;
  private currentAmount = 0;

  constructor() {
    const width = 115;
    const height = 120;
    super({
      width,
      height,
      image: 'assets/ui/match2/common/button_item.png',
      tracking: {
        feature: Feature.MATCH2._,
        $subFeature: Feature.MATCH2.POWERUP_SELECT,
      },
      onDown: () => {
        if (this.infinite) return;
        const anim = animate(this).clear();
        const baseX = -width * 0.5;

        if (this.locked) {
          anim
            .now({ offsetX: baseX })
            .then({ offsetX: baseX - 3 }, 30)
            .then({ offsetX: baseX + 3 }, 50)
            .then({ offsetX: baseX - 3 }, 50)
            .then({ offsetX: baseX + 3 }, 50)
            .then({ offsetX: baseX }, 30);
        } else anim.then({ scale: 0.9 }, 50);
      },
      onUp: () => {
        if (this.locked || this.infinite) return;
        animate(this).clear().then({ scale: 1 }, 200);
      },
    });

    this.onClick = this.handleClick.bind(this);

    this.powerupIcon = new ImageScaleView({
      superview: this,
      width: 75,
      height: 75,
      centerOnOrigin: true,
      x: width * 0.5,
      y: height * 0.5,
    });

    this.badge = new Match2ButtonBadge({
      superview: this,
      scale: 1.2,
      x: width - 12,
      y: 15,
    });

    this.timerBanner = new ImageScaleView({
      superview: this,
      image: 'assets/ui/match2/frenzy/common/badge_frenzy_unlimited.png',
      width: 80,
      height: 37,
      centerOnOrigin: true,
      centerAnchor: true,
      x: width * 0.5,
      y: height - 2,
      scale: 1.3,
      visible: false,
    });

    this.timer = new LangBitmapFontTextShadowView({
      tag: 'powerupFrenzyTimer',
      superview: this,
      ...uiConfig.hud.energyText,
      size: 17,
      color: '#FFFFFF',
      width: 80,
      font: bitmapFonts(uiConfig.hud.energyText.fontName),
      align: 'center',
      centerAnchor: true,
      centerOnOrigin: true,
      x: width * 0.5 + 10,
      y: height - 9,
      visible: false,
      textShadowOffsetX: 0,
      textShadowOffsetY: 2,
      textShadowColor: '#26751E',
    });
  }

  public setType(type: Match2PowerupType) {
    this.type = type;
    if (
      type === 'cube' &&
      isSuperLollipopActive(StateObserver.getState().user)
    ) {
      new SuperLollipopIcon({
        superview: this.powerupIcon,
        x: this.powerupIcon.style.width / 2,
        y: this.powerupIcon.style.height / 2,
      });
    } else {
      this.powerupIcon.setImage(getMatch2AssetPath(getPowerBlockMap()[type]));
    }
    this.refresh();
  }

  public setLocked(locked: boolean) {
    this.locked = locked;
    this.refresh();
  }

  private refresh() {
    let type: Match2ButtonBadgeState = this.amount > 0 ? 'count' : 'add';

    if (this.locked && !this.infinite) type = 'locked';
    else if (this.selected || this.infinite) type = 'selected';

    this.badge.setState(type);
    this.updateButtonImage(
      `assets/ui/match2/common/button_item${
        type === 'selected' ? '_selected' : ''
      }.png`,
    );
    this.badge.setCount(this.infinite ? Infinity : this.amount);
    this.currentAmount = this.amount;
  }

  private async handleClick() {
    if (this.locked || this.infinite) return;
    sounds.playSound(this.selected ? 'cancel' : 'ok');

    if (this.amount) {
      this.selected = !this.selected;
    } else {
      const success = await promptPurchasePowerup(this.type);
      if (success) this.selected = true;
    }

    this.refresh();
  }

  public getType() {
    return this.type;
  }

  public get isSelected() {
    return this.selected || this.infinite;
  }

  public get amount() {
    const { user } = StateObserver.getState();
    const { match2 } = user;
    return match2.powerups[this.type];
  }

  public autoUpdate() {
    const time = this.getRemainingTime();
    const infinite = time > 0;
    const changed =
      infinite !== this.infinite || this.currentAmount !== this.amount;
    this.infinite = infinite;
    this.timerBanner.style.visible = time > 0;
    this.timer.updateOpts({
      visible: time > 0,
      localeText: () => formatDurationShort(time),
    });
    if (changed) this.refresh();
    this.waiter = createWaiter();
    this.waiter.wait(this.autoUpdate.bind(this), duration({ seconds: 1 }));
  }

  public stopAutoUpdate() {
    this.waiter?.clear();
    this.waiter = undefined;
  }

  private getRemainingTime() {
    const user = StateObserver.getState().user;
    const now = StateObserver.now();
    return getMatch2TimedRewardRemainingTime(
      user,
      `powerups-${this.type}`,
      now,
    );
  }
}
